import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const TITLE = "Pluggz vs Amazon Influencer Program";
const DESCRIPTION =
  "A side-by-side comparison of the Amazon Influencer Program and the Pluggz editorial storefront model — conversion rates, commissions, brand trust, and where each model wins.";
const PATH = "/journal/pluggz-vs-amazon-influencer-program";

export const Route = createFileRoute("/journal_/pluggz-vs-amazon-influencer-program")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-21",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: TITLE, item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav aria-label="Breadcrumb" className="relative mx-auto max-w-3xl px-6 pt-10">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li><Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Pluggz vs Amazon</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Comparison · 9 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            Pluggz vs the <span className="italic shimmer">Amazon Influencer Program</span>
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 text-sm text-muted-foreground">By the Pluggz Editorial Team · June 2026</p>
        </Reveal>
      </header>

      <Reveal>
        <div className="relative mx-auto mb-12 max-w-5xl px-6">
          <div className="aspect-[16/7] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-accent/30 via-card to-background ring-1 ring-border/60" />
        </div>
      </Reveal>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing space-y-6 leading-relaxed">
          <p className="text-lg">
            If you are looking for an <em>Amazon Influencer Program alternative</em>, you are part of a growing wave of creators who have outgrown the marketplace model. Amazon's program was the on-ramp for an entire generation of storefront creators, but the ceiling is starting to show: thin commissions, no audience ownership, and a storefront that looks identical to every other creator's. This guide compares the two models head to head so you can decide where to build next.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The short version</h2>
          <p className="text-muted-foreground">
            The Amazon Influencer Program is the fastest way to publish a storefront. Pluggz is the highest-conversion way to run one. Amazon trades long-term ownership and margin for catalog scale and brand recognition. Pluggz trades catalog scale for editorial control, direct brand relationships, and commissions that typically run two to five times higher per sale.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Conversion rate</h2>
          <p className="text-muted-foreground">
            Amazon storefronts convert on Amazon's terms. The shopper lands on your page, scrolls a grid of identical product tiles, and is one tap away from a competing recommendation or a sponsored product. Industry-reported conversion sits in the low single digits for most influencer storefronts.
          </p>
          <p className="text-muted-foreground">
            Pluggz storefronts convert closer to a curated magazine. Editorial layouts, hand-vetted products, and direct brand checkout remove most of the comparison-shopping friction. Creators on Pluggz routinely see conversion rates two to three times higher than the marketplace benchmark — not because the audience is different, but because the path from inspiration to purchase is shorter.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Commission economics</h2>
          <p className="text-muted-foreground">
            Amazon's commission table runs from roughly one to ten percent depending on category, with most fashion and beauty categories sitting at the low end. Rates have trended down, not up, since the program launched.
          </p>
          <p className="text-muted-foreground">
            Pluggz works with brands directly, and rates are negotiated per partnership rather than dictated by a marketplace. Most categories sit in the eight to twenty percent range, with higher tiers for exclusive launches. The compounding effect over a year is significant: a creator earning a one percent rate on Amazon needs ten times the volume to match a ten percent Pluggz partnership.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Brand trust and editorial control</h2>
          <p className="text-muted-foreground">
            On Amazon, every storefront uses the same template. The product photography is whatever the brand uploaded to its listing. The copy is the listing's copy. Your taste is reduced to which tiles you placed in which row.
          </p>
          <p className="text-muted-foreground">
            Pluggz gives you the page. Your photography, your copy, your collections, your aesthetic. Brands you feature are vetted by the editorial team, which means the trust your audience extends to you is not undercut by a dropshipped product two tiles away. The storefront reads as a publication, and publications convert because readers trust the editor.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Audience ownership</h2>
          <p className="text-muted-foreground">
            Amazon owns the customer relationship. You do not see the email, you do not see the repeat purchase, and you cannot retarget a visitor who did not convert. The traffic is yours; the relationship is not.
          </p>
          <p className="text-muted-foreground">
            Pluggz storefronts live on your own surface. You see who visited, what they engaged with, and which collections converted. That data is the difference between renting an audience and building one.
          </p>

          <h2 className="mt-12 font-serif text-3xl">When Amazon is still the right call</h2>
          <p className="text-muted-foreground">
            If your audience shops almost exclusively on Amazon, if your category is dominated by Amazon-only SKUs, or if you need to publish a storefront tomorrow with zero setup, the Amazon Influencer Program is still the fastest path. It is a sensible starting point. Most successful Pluggz creators kept their Amazon storefront live during the transition and migrated their top-converting products first.
          </p>

          <h2 className="mt-12 font-serif text-3xl">When Pluggz is the better play</h2>
          <p className="text-muted-foreground">
            If you care about margin per sale, if your audience trusts your taste more than the marketplace, or if you want to build relationships with brands rather than with a catalog, Pluggz is the long-term home. The editorial model rewards creators who treat their storefront as a media property — and pays them accordingly.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The bottom line</h2>
          <p className="text-muted-foreground">
            The Amazon Influencer Program built the category. The next generation of creator storefronts is being built off-marketplace, with editorial discipline and direct brand economics. Whether you run both in parallel or move fully to an editorial model, the question is no longer whether to diversify — it is how quickly.
          </p>

          <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
            <p className="editorial-eyebrow">Keep reading</p>
            <h3 className="mt-3 font-serif text-2xl">More from the Journal</h3>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              <Link to="/journal" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                Back to the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}